import { useEffect } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';

// Fokus im Einstellungen-Pop-up (AE-1 Nachtrag 3, SPEC AE-03): Tab und Umschalt+Tab bleiben im Pop-up; beim Schließen
// kehrt der Fokus zum Auslöser zurück, sonst auf den Kontoknopf, sonst auf den Hauptinhalt.

// Plan 1a W-22 (N-0): der Kontoknopf heißt „Konto, Einstellungen und Firma“ (components/UserMenu.tsx KONTO_KNOPF). Der Wert
// steht hier ein zweites Mal, damit fokus.ts ohne die Menükomponente lädt; frontend/tests/welle0-rollen.test.mjs hält beide gleich.
const KONTO_KNOPF = 'Konto, Einstellungen und Firma';

// Das nächste Ziel in einer Liste von anzahl fokussierbaren Elementen; index -1: der Fokus liegt auf keinem davon.
export function naechstesZiel(anzahl: number, index: number, rueckwaerts: boolean): number {
  if (anzahl <= 0) return -1;
  if (index < 0) return rueckwaerts ? anzahl - 1 : 0;
  return rueckwaerts ? (index - 1 + anzahl) % anzahl : (index + 1) % anzahl;
}

const FOKUSSIERBAR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
// Sichtbar heißt: gerendert, nicht über visibility versteckt (die Seitenleiste hält beide Formen im DOM) und nicht in einem
// zugeklappten details (dessen eigene summary bleibt erreichbar).
const sichtbar = (el: Element) => el.getClientRects().length > 0 && getComputedStyle(el).visibility === 'visible' && !el.closest('details:not([open]) > :not(summary)');
// Ein Dialog über dem Pop-up (Portaldialog, natives <dialog>) behält Tastatur und Escape für sich (SPEC AE-16).
export const dialogDarueber = () => !!document.querySelector('.editorial-modal-backdrop, dialog[open]');

function fokussierbare(wurzel: HTMLElement): HTMLElement[] {
  return [...wurzel.querySelectorAll<HTMLElement>(FOKUSSIERBAR)].filter(el => {
    if (!sichtbar(el)) return false;
    // Eine Gruppe von Optionsfeldern ist ein Tabulatorhalt: das gewählte, sonst das erste.
    if (el instanceof HTMLInputElement && el.type === 'radio' && el.name) {
      const gruppe = [...wurzel.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${CSS.escape(el.name)}"]`)];
      return el === (gruppe.find(r => r.checked) ?? gruppe[0]);
    }
    return true;
  });
}

// Tab und Umschalt+Tab im Pop-up: immer selbst weiterreichen, damit der Fokus nie in den (inerten) Hintergrund oder
// aus dem Dokument fällt.
export function tabImDialog(event: KeyboardEvent | ReactKeyboardEvent, wurzel: HTMLElement) {
  if (event.key !== 'Tab' || dialogDarueber()) return;
  const liste = fokussierbare(wurzel);
  event.preventDefault();
  if (!liste.length) return;
  const aktiv = document.activeElement;
  let index = liste.indexOf(aktiv as HTMLElement);
  let ziel: number;
  if (index === -1 && aktiv && wurzel.contains(aktiv)) {
    // Fokus auf einem nicht anspringbaren Element (Überschrift): weiter ab seiner Stelle im Dokument.
    const davor = liste.filter(el => aktiv.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING).length;
    ziel = event.shiftKey ? (davor > 0 ? davor - 1 : liste.length - 1) : (davor < liste.length ? davor : 0);
  } else ziel = naechstesZiel(liste.length, index, event.shiftKey);
  liste[ziel].focus();
}

// Merkt den Auslöser beim Öffnen und gibt ihm beim Schließen den Fokus zurück. Der Hintergrund ist beim Abbau noch
// inert; deshalb erst nach dem Aufräumen des Rahmens.
export function useFokusRueckgabe(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    const ausloeser = document.activeElement instanceof HTMLElement && !ref.current?.contains(document.activeElement) ? document.activeElement : null;
    return () => {
      window.setTimeout(() => {
        const erreichbar = (el: Element | null): el is HTMLElement => el instanceof HTMLElement && el.isConnected && sichtbar(el) && !el.closest('[inert]');
        const profil = [...document.querySelectorAll(`[aria-label="${KONTO_KNOPF}"]`)].find(erreichbar);
        const ziel = erreichbar(ausloeser) && ausloeser !== document.body ? ausloeser : profil ?? document.querySelector<HTMLElement>('main');
        if (ziel instanceof HTMLElement) { if (ziel.tagName === 'MAIN' && !ziel.hasAttribute('tabindex')) ziel.setAttribute('tabindex', '-1'); ziel.focus(); }
      }, 0);
    };
  }, [ref]);
}
